import type { ActionableResult, ActionExecutionContext } from './ActionRegistry';

const ACTION_OBJECTS_KEY = '__WEALTHLANE_ACTION_OBJECTS__';

function getActionObjects(): Map<string, Action> {
  const w = window as any;
  if (!w[ACTION_OBJECTS_KEY]) {
    w[ACTION_OBJECTS_KEY] = new Map<string, Action>();
  }
  return w[ACTION_OBJECTS_KEY] as Map<string, Action>;
}

/**
 * The core contract for every action in the system.
 *
 * Each action is a self-contained strategy object that knows its identity,
 * its field requirements, its permission gate, its row-level guard, and
 * how to execute itself. The table is dumb — it delegates everything to
 * the Action object via the ActionRegistry.
 *
 * Naming convention: the `id` MUST follow VERB_DOMAIN or VERB_MODIFIER_DOMAIN
 * where the LAST word is always the domain name (e.g. 'participant', 'award').
 * This convention enables auto-loading of domain modules by the registry.
 */
export type ActionResult = void | Record<string, unknown>;

export interface Action {
  /** Semantic identity. Last word = domain. e.g. 'EDIT_PARTICIPANT', 'VIEW_AWARD'. */
  readonly id: string;

  /** Fields this action reads from the row. Declares the data contract. */
  readonly requires: readonly string[];

  /**
   * Global gate — is this action available at all?
   * No parameters. Reads permissions lazily from ambient singletons (Redux store).
   * Returns false → the action button is completely hidden for ALL rows.
   */
  isAvailable(): boolean;

  /**
   * Row-level gate — can this specific row have this action?
   * Returns visibility + enabled state per row.
   */
  isActionable(row: Record<string, unknown>): ActionableResult;

  /**
   * Execute the action. Self-contained.
   * - For row/bulk actions: call API, show toast, emit intent. Return void.
   * - For data actions: fetch and return data for the view engine.
   */
  execute(ctx: ActionExecutionContext): ActionResult | Promise<ActionResult>;
}

/**
 * Base class providing default implementations so concrete actions only
 * override what they need.
 *
 * Decorate subclasses with `@action` to auto-register them.
 */
export abstract class AbstractAction implements Action {
  abstract readonly id: string;
  readonly requires: readonly string[] = [];

  isAvailable(): boolean {
    return true;
  }

  isActionable(_row: Record<string, unknown>): ActionableResult {
    return { visible: true, enabled: true };
  }

  abstract execute(ctx: ActionExecutionContext): ActionResult | Promise<ActionResult>;
}

/**
 * Class decorator that auto-registers an AbstractAction subclass.
 *
 * Usage:
 * ```ts
 * @action
 * class ViewParticipantAction extends AbstractAction {
 *   readonly id = 'VIEW_PARTICIPANT';
 *   // ...
 * }
 * ```
 */
export function action<T extends new (...args: any[]) => AbstractAction>(
  Class: T
): T {
  const instance = new Class();
  const objects = getActionObjects();
  if (!objects.has(instance.id)) {
    objects.set(instance.id, instance);
  }
  return Class;
}

/**
 * Typed intent emitted by actions that need to open a modal.
 * The ActionIntentHandler in the AppShell subscribes to these and
 * lazy-loads the appropriate modal component.
 */
export interface ActionIntent {
  type: 'OPEN_MODAL';
  actionId: string;
  payload: Record<string, unknown>;
}
